import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { ActivityIndicator, Checkbox, Text } from 'react-native-paper';
import { Easing, ReduceMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ArrowRightIcon, CheckIcon, CloseIcon } from '@/assets';
import { WEB_MAX_WIDTH } from '@/constants/web';
import { getAllDescendantIds } from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { useGeoAreaTypeLabel } from '@/features/geo-area/hooks/useGeoAreaTypeLabel';
import { BottomModal } from '@/features/popup/components/BottomModal';
import type { BottomModalProps } from '@/features/popup/components/BottomModal';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { RotatableView } from '@/shared/components/animated/RotatableView';
import { SYNTHETIC_ROOT_AREA_ID } from '@/shared/components/forms/geoAreaRoot';
import type { useGeoAreaSelection } from '@/shared/components/forms/useGeoAreaSelection';
import { isIOS, isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = {
  /** The full selection state from {@link useGeoAreaSelection}. */
  selection: ReturnType<typeof useGeoAreaSelection>;
  title: string;
  isLoading: boolean;
  /** Confirms the pending selection and writes it back to the form. */
  onDone: () => void;
};

const IOS_GEO_AREA_SHEET_ANIMATION = {
  duration: 500,
  easing: Easing.inOut(Easing.cubic),
  reduceMotion: ReduceMotion.System
};

/**
 * Bottom-sheet modal for picking geo areas from a tree (drill-down with
 * breadcrumb, whole-area checkbox, sub-area list with select-all).
 * Shared between the outlined form field and the list-item variant — the
 * whole selection state is passed through the {@link useGeoAreaSelection}
 * result, so this component has a stable, tiny prop surface.
 */
export const GeoAreaSelectionModal: FC<Props> = ({
  selection,
  title,
  isLoading,
  onDone
}) => {
  const {
    isOpen: isVisible,
    handleClose: onClose,
    navStack,
    currentNode,
    currentAreas,
    pendingIds,
    allSubAreasSelected,
    handleGoBack: onGoBack,
    handleDrillDown: onDrillDown,
    toggle: onToggle,
    handleToggleAll: onToggleAll,
    getStatus,
    getSelectedDescendantCount
  } = selection;
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme, isWeb), [theme]);
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization.geoAreaPicker'
  });
  const getGeoAreaTypeLabel = useGeoAreaTypeLabel();
  const webSnapPoint = Math.min(
    (height - insets.top) * 0.9,
    280 + currentAreas.length * 56
  );
  const modalProps: BottomModalProps = {
    isVisible,
    title,
    titleAlign: 'left',
    onClose,
    showCloseButton: false,
    maxWidth: WEB_MAX_WIDTH,
    snapPoints: isWeb ? [webSnapPoint] : undefined,
    enableDynamicSizing: isWeb ? false : undefined,
    animationConfigs: isIOS ? IOS_GEO_AREA_SHEET_ANIMATION : undefined,
    maxDynamicContentSizeRatio: isWeb ? undefined : 0.7,
    scrollComponent: isWeb ? undefined : BottomSheetScrollView,
    fillScrollContent: isWeb,
    stickyHeader: !isWeb,
    endButtons: [
      {
        icon: CloseIcon,
        mode: 'outlined',
        onPress: onClose
      },
      {
        icon: CheckIcon,
        mode: 'contained',
        disabled: isLoading,
        shouldCloseWhenPressed: true,
        onPress: onDone,
        theme: {
          colors: {
            surfaceVariant: theme.colors.error,
            primary: theme.colors.onPrimary
          }
        }
      }
    ]
  };

  if (isLoading) {
    return (
      <BottomModal {...modalProps}>
        <View style={styles.body}>
          <View
            accessibilityRole="progressbar"
            accessibilityLabel={t('loading')}
            accessibilityHint={t('loading')}
            style={styles.loading}
          >
            <ActivityIndicator size={48} color={theme.colors.primary} />
          </View>
        </View>
      </BottomModal>
    );
  }

  return (
    <BottomModal {...modalProps}>
      <View style={styles.body}>
        {navStack.length > 0 && (
          <Pressable
            accessibilityRole="button"
            onPress={onGoBack}
            style={styles.breadcrumb}
          >
            <RotatableView changeRotation>
              <ArrowRightIcon
                width={20}
                height={20}
                fill={theme.colors.onSurface}
              />
            </RotatableView>
            <Text variant="labelLarge" numberOfLines={1}>
              {navStack.map((node) => node.name).join(' / ')}
            </Text>
          </Pressable>
        )}

        {currentNode && (
          <Pressable
            style={styles.wholeArea}
            accessibilityRole="checkbox"
            accessibilityState={{
              checked: pendingIds.includes(currentNode.id)
            }}
            onPress={() => onToggle(currentNode)}
          >
            {/* Android variant: the iOS variant renders no visible control
                when unchecked (checkmark glyph only), leaving rows without a
                tap-affordance. */}
            <Checkbox.Android
              status={getStatus(currentNode)}
              color={theme.colors.primary}
            />
            <View style={styles.rowInfo}>
              <Text variant="titleMedium">
                {currentNode.id === SYNTHETIC_ROOT_AREA_ID
                  ? currentNode.name
                  : t('wholeArea', { area: currentNode.name })}
              </Text>
              <Text
                variant="labelSmall"
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {t('wholeAreaDescription')}
              </Text>
            </View>
          </Pressable>
        )}

        {currentAreas.length > 0 && (
          <View style={styles.subAreasHeader}>
            <Text variant="titleSmall" style={styles.subAreasTitle}>
              {t('subAreas')}
            </Text>
            <Pressable
              onPress={onToggleAll}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={
                allSubAreasSelected ? t('deselectAll') : t('selectAll')
              }
              accessibilityHint={t('subAreas')}
            >
              <Text
                variant="labelMedium"
                style={{ color: theme.colors.primary }}
              >
                {allSubAreasSelected ? t('deselectAll') : t('selectAll')}
              </Text>
            </Pressable>
          </View>
        )}

        {currentAreas.map((area) => {
          const hasChildren = (area.childGeoAreas?.length ?? 0) > 0;
          const isSelected = pendingIds.includes(area.id);
          const descendantCount = hasChildren
            ? getAllDescendantIds(area).length - 1
            : 0;
          const selectedCount = getSelectedDescendantCount(area);

          return (
            <View key={area.id} style={styles.row}>
              <Pressable
                style={styles.rowLeft}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isSelected }}
                onPress={() => onToggle(area)}
              >
                <Checkbox.Android
                  status={getStatus(area)}
                  color={theme.colors.primary}
                />
                <View style={styles.rowInfo}>
                  <Text variant="bodyLarge" numberOfLines={1}>
                    {area.name}
                  </Text>
                  <Text
                    variant="labelSmall"
                    style={{ color: theme.colors.onSurfaceVariant }}
                  >
                    {getGeoAreaTypeLabel(area.geoAreaType)}
                  </Text>
                </View>
              </Pressable>

              {hasChildren && (
                <Pressable
                  onPress={() => onDrillDown(area)}
                  accessibilityRole="button"
                  accessibilityLabel={area.name}
                  accessibilityHint={getGeoAreaTypeLabel(area.geoAreaType)}
                  style={styles.drillDown}
                >
                  {selectedCount > 0 && (
                    <Text
                      variant="labelMedium"
                      style={{ color: theme.colors.onSurfaceVariant }}
                    >
                      {t('selectionOf', {
                        selected: selectedCount,
                        total: descendantCount
                      })}
                    </Text>
                  )}
                  <ArrowRightIcon
                    width={20}
                    height={20}
                    fill={theme.colors.onSurfaceVariant}
                  />
                </Pressable>
              )}
            </View>
          );
        })}
      </View>
    </BottomModal>
  );
};

const makeStyles = (theme: AppTheme, isWeb: boolean) =>
  StyleSheet.create({
    body: {
      paddingHorizontal: 16,
      paddingBottom: isWeb ? 16 : 12,
      gap: 4
    },
    loading: {
      alignItems: 'center',
      paddingVertical: 24
    },
    breadcrumb: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: isWeb ? 12 : 8
    },
    wholeArea: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: isWeb ? 16 : 12,
      backgroundColor: theme.colors.surfaceContainerHighest,
      borderRadius: 8
    },
    subAreasHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: isWeb ? 16 : 10,
      paddingBottom: 4
    },
    subAreasTitle: {
      color: theme.colors.onSurfaceVariant
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.outlineVariant
    },
    rowLeft: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center'
    },
    rowInfo: {
      flex: 1,
      marginLeft: 4
    },
    drillDown: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      padding: 8
    }
  });
